import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageConfig from '@/component/layout/PageConfig';
import SearchBar from '@/component/input/SearchBar';
import BorderButton from '@/component/button/BorderButton';
import SolidButton from '@/component/button/SolidButton';
import Pagination from '@/component/pagination/Pagination';
import BCGUploadModal, { BCGUploadModalRef } from '@/component/mentalHealth/BCGUploadModal';
import BCGSessionModal, { BCGSessionModalRef } from '@/component/mentalHealth/BCGSessionModal';
import useQueryGetUsersSimple, { IUser } from '@/api/user/getUsersSimple';
import useDebounce from '@/util/hook/useDebounce';
import { mentalHealthStore } from '@/store/MentalHealthStore';
import { isEmptyArray, isNotEmptyArray } from '@/util/array';
import { useGetMindHealthSessions, apiGetSessionBCGData, MindHealthSession } from '@/api/mentalHealth/mindHealthSession';
import { analyzeBCGData } from '@/util/bcg';
import { AnalysisParams, MentalHealthReport } from '@/util/bcg/types';
import { useUserSessionCounts } from '@/hooks/useUserSessionCounts';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

const PAGE_SIZE = 10;

// 사용자별 건강 데이터 인터페이스
interface UserHealthData extends IUser {
  healthScore?: number;
  healthStatus?: string;
  stressLevel?: string;
  lastAnalysisDate?: string;
  age?: number;
  hasAnalysisData: boolean;
  hasBackendData?: boolean; // 백엔드에 세션 데이터가 있는지
  sessionCount?: number; // 백엔드 세션 개수
}

interface RecentAnalysis {
  id: string | number;
  userId: number;
  userName: string;
  analysisDate: string;
  filename: string;
  report?: MentalHealthReport;
  isFromBackend: boolean;
  sessionId?: string;
  session?: MindHealthSession;
}

interface LatestDataCache {
  [key: number]: {
    healthScore?: number;
    healthStatus?: string;
    stressLevel?: string;
  };
}


const MentalHealthDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const uploadModalRef = useRef<BCGUploadModalRef>(null);
  const sessionModalRef = useRef<BCGSessionModalRef>(null);

  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState(Number(searchParams.get('id')) || 0);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [latestDataCache, setLatestDataCache] = useState<LatestDataCache>({});

  const debouncedSearchText = useDebounce(search, 500);

  // 전체 회원 데이터 가져오기 (Simple API)
  const { data: allUsers, isLoading } = useQueryGetUsersSimple();

  // 선택된 사용자의 세션 목록 가져오기 (COMPLETED 상태만)
  const { data: sessionsData, refetch: refetchSessions } = useGetMindHealthSessions(
    userId || 0,
    { status: 'COMPLETED', pageSize: 50, sortBy: 'startTime:desc' },
    userId > 0
  );

  // 검색 필터링 및 가나다순 정렬
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];

    let filtered = allUsers;

    // 검색어 필터링
    if (debouncedSearchText) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(debouncedSearchText.toLowerCase())
      );
    }

    // 가나다순 정렬 (한글 이름 기준)
    return filtered.sort((a, b) => a.name.localeCompare(b.name, 'ko-KR'));
  }, [allUsers, debouncedSearchText]);


  // 회원 데이터와 건강 데이터 합치기 (localStorage만 우선 사용)
  const usersWithHealth = useMemo(() => {
    if (!filteredUsers) return [];

    const allReports = mentalHealthStore.getAllReports();

    return filteredUsers.map((user): UserHealthData => {
      // localStorage에서 최신 리포트 찾기 (날짜 내림차순 정렬)
      const userReports = allReports
        .filter(r => r.userId === user.id)
        .sort((a, b) => new Date(b.analysisDate).getTime() - new Date(a.analysisDate).getTime());

      const latestReport = userReports[0];

      // 나이 계산
      const birthYear = user.birthDate ? parseInt(user.birthDate.split('-')[0]) : null;
      const age = birthYear ? new Date().getFullYear() - birthYear : null;

      return {
        ...user,
        healthScore: latestReport ? Math.round(latestReport.report.mindScore.score) : undefined,
        healthStatus: latestReport ? latestReport.report.decisionLabel : undefined,
        stressLevel: latestReport ? latestReport.report.stressAnalysis.category : undefined,
        lastAnalysisDate: latestReport ? latestReport.analysisDate : undefined,
        age: age || undefined,
        hasAnalysisData: !!latestReport,
        hasBackendData: false, // 나중에 업데이트됨
        sessionCount: 0, // 나중에 업데이트됨
      };
    });
  }, [filteredUsers]);

  // 페이지네이션
  const totalPages = Math.ceil(usersWithHealth.length / PAGE_SIZE);
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return usersWithHealth.slice(start, end);
  }, [usersWithHealth, currentPage]);

  // 페이지네이션된 사용자들의 세션 개수 조회
  const userIds = useMemo(() => paginatedUsers.map(u => u.id), [paginatedUsers]);
  const { sessionCounts } = useUserSessionCounts(userIds);

  // 세션 개수로 사용자 데이터 업데이트
  const paginatedUsersWithSessions = useMemo(() => {
    return paginatedUsers.map(user => {
      const sessionInfo = sessionCounts.get(user.id);
      return {
        ...user,
        hasBackendData: sessionInfo?.hasData || false,
        sessionCount: sessionInfo?.sessionCount || 0,
        hasAnalysisData: user.hasAnalysisData || (sessionInfo?.hasData || false),
      };
    });
  }, [paginatedUsers, sessionCounts]);
  
  // 선택된 사용자 (백엔드 세션 정보 포함)
  const selectedUser = useMemo(() => {
    const userInList = paginatedUsersWithSessions.find(u => u.id === userId) || usersWithHealth.find(u => u.id === userId);
    if (!userInList) return undefined;
    
    // 이 부분은 selectedUser가 다시 계산될 때마다 최신 세션 정보를 반영하기 위해 유지합니다.
    const sessionInfo = sessionCounts.get(userInList.id);
    return {
      ...userInList,
      hasBackendData: sessionInfo?.hasData || userInList.hasBackendData || false,
      sessionCount: sessionInfo?.sessionCount || userInList.sessionCount || 0,
      hasAnalysisData: userInList.hasAnalysisData || (sessionInfo?.hasData || false),
    };
  }, [paginatedUsersWithSessions, usersWithHealth, userId, sessionCounts]);


  // 선택된 사용자의 최근 분석 기록 (백엔드 세션 + localStorage 결합)
  const recentAnalyses: RecentAnalysis[] = useMemo(() => {
    if (!selectedUser) return [];

    // 백엔드 세션 데이터 (COMPLETED 상태만)
    const sessions = sessionsData?.data || [];
    const completedSessions = sessions.filter(session => session.status === 'COMPLETED');

    const sessionAnalyses: RecentAnalysis[] = completedSessions.map((session) => ({
      id: session.sessionId,
      sessionId: session.sessionId,
      userId: selectedUser.id,
      userName: selectedUser.name,
      analysisDate: session.endTime || session.startTime,
      filename: `Session_${session.sessionId}`,
      isFromBackend: true,
      session, // 전체 세션 정보 포함
      // localStorage에서 캐시된 분석 결과가 있는지 확인
      report: mentalHealthStore.getReportById(session.sessionId)?.report,
    }));

    // localStorage 데이터 (백엔드 세션 ID와 중복되지 않는 것만)
    const sessionIds = new Set(sessions.map(s => s.sessionId));
    const localReports: RecentAnalysis[] = mentalHealthStore.getAllReports()
      .filter(r => r.userId === selectedUser.id && !sessionIds.has(r.id.toString()))
      .map((r) => ({
        ...r,
        isFromBackend: false,
      }));

    // 합치고 날짜순 정렬
    const combined = [...sessionAnalyses, ...localReports]
      .sort((a, b) => new Date(b.analysisDate).getTime() - new Date(a.analysisDate).getTime())
      .slice(0, 10);

    return combined;
  }, [selectedUser, sessionsData]);

  // 선택된 사용자의 최신 리포트 (첫 번째 분석)
  const latestReport = useMemo(() => {
    return recentAnalyses[0] || null;
  }, [recentAnalyses]);

  // 최신 데이터 캐시 업데이트
  useEffect(() => {
    if (selectedUser && latestReport) {
      setLatestDataCache(prev => ({
        ...prev,
        [selectedUser.id]: {
          healthScore: latestReport.report ? Math.round(latestReport.report.mindScore.score) : 0,
          healthStatus: latestReport.report ? latestReport.report.decisionLabel : 'insufficient_data',
          stressLevel: latestReport.report ? latestReport.report.stressAnalysis.category : 'insufficient_data',
        }
      }));
    }
  }, [selectedUser, latestReport]);

  // 첫 번째 사용자 자동 선택
  useEffect(() => {
    if (userId <= 0 && paginatedUsersWithSessions.length > 0) {
      setUserId(paginatedUsersWithSessions[0].id);
    }
  }, [paginatedUsersWithSessions, userId]);

  // 검색어 변경 시 첫 페이지로
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  // URL 파라미터 업데이트
  useEffect(() => {
    const params: any = {};
    if (userId > 0) params.id = userId.toString();
    if (currentPage > 1) params.page = currentPage.toString();
    setSearchParams(params);
  }, [userId, currentPage, setSearchParams]);

  // 분석 완료된 회원 수
  const analyzedUsersCount = useMemo(() => {
    const allReports = mentalHealthStore.getAllReports();
    const uniqueUserIds = new Set(allReports.map(r => r.userId));
    return uniqueUserIds.size;
  }, []);

  // 세션 데이터로부터 리포트 로드
  const loadReportFromSession = async (sessionId: string) => {
    if (!userId || !selectedUser) {
      return null;
    }

    try {
      // localStorage에서 먼저 확인
      const cachedReport = mentalHealthStore.getReportById(sessionId);
      if (cachedReport) {
        return cachedReport;
      }

      // 백엔드에서 BCG 데이터 가져오기
      const bcgDataResponse = await apiGetSessionBCGData(userId, sessionId);

      if (!bcgDataResponse.data) {
        return null;
      }

      // BCG 데이터 변환 및 분석
      const bcgData = bcgDataResponse.data.bcgData;
      const csvData = convertBCGDataToCSVFormat(bcgData);

      const params: AnalysisParams = {
        windowSec: 60,
        strideSec: 15,
        mode: 'absolute',
        calibrationPairs: [],
      };

      const report = analyzeBCGData(csvData, params);

      if (!report) {
        return null;
      }

      // localStorage에 캐싱 (sessionId를 ID로 사용)
      const storedReport = mentalHealthStore.saveReport(
        `Session_${sessionId}`,
        report,
        userId,
        selectedUser.name,
        sessionId // customId로 sessionId 전달
      );

      return storedReport;
    } catch (error) {
      console.error('리포트 로드 실패:', error);
      return null;
    }
  };

  // BCG 데이터 변환 함수
  const convertBCGDataToCSVFormat = (bcgData: number[], chunkSize: number = 256) => {
    const csvRows: { deviceTime: string, bcgMerge: string }[] = [];
    const totalChunks = Math.ceil(bcgData.length / chunkSize);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, bcgData.length);
      const chunkData = bcgData.slice(start, end);

      csvRows.push({
        deviceTime: new Date(Date.now() + i * 1000).toISOString(),
        bcgMerge: JSON.stringify({ data: chunkData }),
      });
    }

    return csvRows;
  };

  const handleUploadSuccess = () => {
    // 업로드 성공 후 세션 목록 새로고침
    refetchSessions();
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // 선택된 회원의 최신 리포트 상세 페이지로 이동
  const handleViewDetailReport = async () => {
    if (!latestReport) return;

    // 백엔드 세션인 경우 먼저 로드
    if (latestReport.isFromBackend && 'sessionId' in latestReport && latestReport.sessionId) {
      await loadReportFromSession(latestReport.sessionId);
    }

    navigate(`/mental-health/reports/${String(latestReport.id)}`);
  };

  // 분석 기록 클릭 핸들러
  const handleAnalysisClick = async (analysis: RecentAnalysis) => {
    if (analysis.isFromBackend && analysis.sessionId) {
      const loadedReport = await loadReportFromSession(analysis.sessionId);

      if (!loadedReport) {
        return;
      }
    }

    navigate(`/mental-health/reports/${String(analysis.id)}`);
  };

  // 선택된 회원의 리포트 목록 페이지로 이동 (필터링)
  const handleViewAllReports = () => {
    if (!selectedUser) return;
    // userId를 쿼리 파라미터로 전달하여 해당 회원 리포트만 표시
    navigate(`/mental-health/reports?userId=${selectedUser.id}`);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' };

    // 영어 키로 직접 비교
    switch (status) {
      case 'healthy':
      case '건강':
        return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500' };
      case 'normal':
      case '보통':
        return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' };
      case 'caution':
      case '주의':
        return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' };
      case 'risk':
      case '위험':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' };
      case 'insufficient_data':
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' };
    }
  };

  const getStressEmoji = (stress?: string) => {
    if (!stress) return '⏳';

    // 영어 키로 직접 비교
    switch (stress) {
      case 'low':
      case '낮음': return '😌';
      case 'normal':
      case '보통': return '😐';
      case 'high':
      case 'very_high':
      case '높음': return '😰';
      default: return '⏳';
    }
  };

  const headerHealthStatus = latestReport?.report ? latestReport.report.decisionLabel : 'insufficient_data';
  const headerStressLevel = latestReport?.report ? latestReport.report.stressAnalysis.category : 'insufficient_data';
  const headerHealthScore = latestReport?.report ? Math.round(latestReport.report.mindScore.score) : 0;


  return (
    <PageConfig showTopNav={true} showFooter={true}>
      {!isLoading && (
        <div className="w-full min-h-screen pb-24">
          {/* 모바일: 회원 리스트 */}
          <div className="bg-white flex flex-col">
            {/* 헤더 */}
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-600 to-teal-600">
              <h2 className="text-lg font-bold text-white mb-3 flex items-center">
                <span className="text-xl mr-2">💚</span>
                {t('mentalHealth.title')}
              </h2>
              <SearchBar
                inputParams={{
                  placeholder: t('mentalHealth.search.placeholder')
                }}
                onChanged={setSearch}
              />
            </div>

            {/* 통계 요약 */}
            <div className="p-3 bg-emerald-50 border-b border-emerald-100">
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-lg p-2 text-center">
                  <div className="text-xl font-bold text-emerald-600">
                    {usersWithHealth.length}
                  </div>
                  <div className="text-[10px] text-gray-600">{t('mentalHealth.stats.totalMembers')}</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center">
                  <div className="text-xl font-bold text-blue-600">
                    {analyzedUsersCount}
                  </div>
                  <div className="text-[10px] text-gray-600">{t('mentalHealth.stats.analyzed')}</div>
                </div>
              </div>
            </div>

            {/* 회원 리스트 */}
            <div className="overflow-y-auto">
              {isEmptyArray(paginatedUsers) && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <div className="text-4xl mb-3">🔍</div>
                  <p className="text-xs">
                    {debouncedSearchText ? t('mentalHealth.empty.search') : t('mentalHealth.empty.noMembers')}
                  </p>
                </div>
              )}

              {isNotEmptyArray(paginatedUsersWithSessions) && (
                <div className="divide-y divide-gray-100">
                  {paginatedUsersWithSessions.map((user: UserHealthData) => {
                    const cachedData = latestDataCache[user.id];
                    const displayData = cachedData ? {
                      healthScore: cachedData.healthScore,
                      healthStatus: cachedData.healthStatus,
                      stressLevel: cachedData.stressLevel,
                    } : {
                      healthScore: user.healthScore,
                      healthStatus: user.healthStatus,
                      stressLevel: user.stressLevel,
                    };

                    const statusColors = getStatusColor(displayData.healthStatus);
                    const genderText = user.gender === 1 ? t('common.gender.male') : user.gender === 2 ? t('common.gender.female') : t('common.gender.none');
                    const isSelected = selectedUser?.id === user.id;

                    return (
                      <div
                        key={user.id}
                        onClick={() => setUserId(user.id)}
                        className={`p-3 cursor-pointer transition-all ${isSelected
                            ? 'bg-emerald-50 border-l-2 border-emerald-500'
                            : 'hover:bg-gray-50 border-l-2 border-transparent'
                          }`}
                      >
                        <div className="flex items-center space-x-2">
                          {/* 상태 표시등 */}
                          <div className={`w-2 h-2 rounded-full ${statusColors.dot} ${isSelected ? 'animate-pulse' : ''}`}></div>

                          {/* 아바타 */}
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${user.gender === 2 ? 'from-pink-400 to-rose-400' : 'from-blue-400 to-cyan-400'
                            } flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                            {user.name.charAt(0)}
                          </div>

                          {/* 정보 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <div className="flex items-center space-x-1">
                                <span className="font-bold text-sm text-gray-900 truncate">{user.name}</span>
                                {user.hasBackendData && (
                                  <span className="text-[10px] px-1 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">
                                    ☁️
                                  </span>
                                )}
                              </div>
                              {user.hasAnalysisData ? (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusColors.bg} ${statusColors.text} font-semibold`}>
                                  {displayData.healthStatus ? t(`mentalHealth.status.${displayData.healthStatus}`) : t('mentalHealth.status.pending')}
                                </span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-semibold">
                                  {t('mentalHealth.status.pending')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-1 text-[10px] text-gray-600">
                              <span>{genderText}</span>
                              {user.age && (
                                <>
                                  <span>•</span>
                                  <span>{t('dashboard.notification.age', { count: user.age })}</span>
                                </>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-[10px] text-gray-500">
                                {getStressEmoji(displayData.stressLevel)} {displayData.stressLevel ? t(`mentalHealth.stress.${displayData.stressLevel}`) : t('mentalHealth.status.pending')}
                              </span>
                              {user.hasAnalysisData && (
                                <span className="text-[10px] font-semibold text-emerald-600">
                                  {displayData.healthScore}{t('mentalHealth.score.points')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div className="p-2 border-t border-gray-200">
                <Pagination
                  page_size={PAGE_SIZE}
                  current_page={currentPage}
                  total_page={totalPages}
                  fetchPage={handlePageChange}
                />
              </div>
            )}
          </div>

          {/* 모바일: 상세 정보 (선택된 사용자가 있을 때만) */}
          <div className="overflow-y-auto bg-gray-50">
            {selectedUser ? (
              (selectedUser.hasAnalysisData || recentAnalyses.length > 0) ? (
                // 분석 데이터가 있는 경우 (로컬 또는 백엔드)
                <div className="p-4 space-y-4">
                  {/* 헤더 */}
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-4 text-white shadow-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${selectedUser.gender === 2 ? 'from-pink-300 to-rose-300' : 'from-blue-300 to-cyan-300'
                          } flex items-center justify-center text-lg font-black border-2 border-white shadow-lg`}>
                          {selectedUser.name.charAt(0)}
                        </div>
                        <div>
                          <h1 className="text-lg font-black mb-1">{selectedUser.name}</h1>
                          <div className="flex items-center space-x-2 text-[10px] text-emerald-100">
                            <span>{selectedUser.gender === 1 ? t('common.gender.male') : selectedUser.gender === 2 ? t('common.gender.female') : t('common.gender.none')}</span>
                            {selectedUser.age && (
                              <>
                                <span>•</span>
                                <span>{t('dashboard.notification.age', { count: selectedUser.age })}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-black mb-1">{headerHealthScore}</div>
                        <div className="text-[10px] opacity-90">{t('mentalHealth.score.health')}</div>
                      </div>
                    </div>
                  </div>

                  {/* 주요 지표 */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* 상태 */}
                    <div className="bg-white rounded-xl p-3 shadow-md border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-gray-600 font-medium">{t('mentalHealth.currentStatus')}</span>
                        <span className="text-lg">✅</span>
                      </div>
                      <div className={`text-sm font-bold ${getStatusColor(headerHealthStatus).text}`}>
                        {headerHealthStatus ? t(`mentalHealth.status.${headerHealthStatus}`) : t('mentalHealth.status.pending')}
                      </div>
                    </div>

                    {/* 스트레스 */}
                    <div className="bg-white rounded-xl p-3 shadow-md border border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-gray-600 font-medium">{t('mentalHealth.stress.title')}</span>
                        <span className="text-lg">{getStressEmoji(headerStressLevel)}</span>
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {headerStressLevel ? t(`mentalHealth.stress.${headerStressLevel}`) : t('mentalHealth.status.pending')}
                      </div>
                    </div>
                  </div>

                  {/* 건강 추이 차트 - 모바일 숨김 */}
                  <div className="hidden"></div>

                  {/* HRV 기반 증상 예측 - 모바일 숨김 */}
                  <div className="hidden"></div>

                  {/* 최근 활동 */}
                  <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                      <span className="text-lg mr-2">📝</span>
                      {t('mentalHealth.recentAnalysis')}
                    </h3>
                    {recentAnalyses.length > 0 ? (
                      <>
                        <div className="space-y-2">
                          {recentAnalyses.slice(0, 3).map((analysis) => {
                            const analysisDate = new Date(analysis.analysisDate);
                            return (
                              <div
                                key={analysis.id.toString()}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                onClick={() => handleAnalysisClick(analysis)}
                              >
                                <div className="flex items-center space-x-2">
                                  <div className="text-base">{analysis.isFromBackend ? '☁️' : '📅'}</div>
                                  <div>
                                    <div className="font-semibold text-xs text-gray-900">
                                      {analysisDate.toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR')}
                                    </div>
                                    <div className="text-[10px] text-gray-600">
                                      {analysisDate.toLocaleTimeString(i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'en' ? 'en-US' : 'ko-KR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {analysis.report ? (
                                    <>
                                      <div className="text-right">
                                        <div className="text-base font-bold text-gray-900">{Math.round(analysis.report.mindScore.score)}</div>
                                      </div>
                                      <div className={`px-2 py-0.5 rounded text-[10px] font-semibold ${getStatusColor(analysis.report.decisionLabel).bg} ${getStatusColor(analysis.report.decisionLabel).text}`}>
                                        {t(`mentalHealth.status.${analysis.report.decisionLabel}`)}
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-[10px] text-gray-500">
                                      {t('mentalHealth.clickToLoad')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3">
                          <BorderButton
                            text={t('mentalHealth.recentAnalysis.viewAll')}
                            onClick={handleViewAllReports}
                            className="w-full text-xs py-2"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="text-center text-gray-400 py-8">
                        <div className="text-3xl mb-2">📋</div>
                        <p className="text-xs">{t('mentalHealth.recentAnalysis.empty')}</p>
                      </div>
                    )}
                  </div>

                  {/* 액션 버튼들 */}
                  <div className="grid grid-cols-2 gap-3">
                    <SolidButton
                      text={t('mentalHealth.button.viewDetailReport')}
                      onClick={handleViewDetailReport}
                      className="bg-emerald-600 hover:bg-emerald-700 py-3 text-xs"
                    />
                    <BorderButton
                      text={t('mentalHealth.button.newAnalysis')}
                      onClick={() => sessionModalRef.current?.show()}
                      className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 py-3 text-xs"
                    />
                  </div>
                </div>
              ) : (
                // 분석 데이터가 없는 경우
                <div className="flex flex-col items-center justify-center text-gray-400 p-4">
                  <div className="bg-white rounded-xl p-6 shadow-lg text-center w-full">
                    <div className="text-5xl mb-4">📁</div>
                    <h3 className="text-base font-bold text-gray-900 mb-3">{t('mentalHealth.noData.title')}</h3>
                    <p className="text-xs text-gray-600 mb-2">
                      {t('mentalHealth.noData.desc1', { name: selectedUser.name })}
                    </p>
                    <p className="text-xs text-gray-600 mb-6">
                      {t('mentalHealth.noData.desc2')}
                    </p>

                    {/* 회원 정보 */}
                    <div className="bg-gray-50 rounded-lg p-3 mb-6 text-left">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${selectedUser.gender === 2 ? 'from-pink-400 to-rose-400' : 'from-blue-400 to-cyan-400'
                          } flex items-center justify-center text-white font-bold text-base`}>
                          {selectedUser.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-bold text-sm text-gray-900">{selectedUser.name}</div>
                          <div className="text-[10px] text-gray-600">
                            {selectedUser.gender === 1 ? t('common.gender.male') : selectedUser.gender === 2 ? t('common.gender.female') : t('common.gender.none')}
                            {selectedUser.age && ` • ${t('dashboard.notification.age', { count: selectedUser.age })}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    <SolidButton
                      text={t('mentalHealth.button.uploadBCG')}
                      onClick={() => sessionModalRef.current?.show()}
                      className="w-full py-3 text-xs bg-emerald-600 hover:bg-emerald-700"
                    />
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <div className="text-5xl mb-4">👤</div>
                <h3 className="text-base font-bold text-gray-600 mb-2">{t('mentalHealth.empty.selectMember')}</h3>
                <p className="text-xs text-gray-500">{t('mentalHealth.empty.selectMember.desc')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* BCG 파일 업로드 모달 (기존 CSV 업로드) */}
      <BCGUploadModal
        ref={uploadModalRef}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
        onSuccess={handleUploadSuccess}
      />

      {/* BCG 세션 모달 (실시간 데이터 수집) */}
      <BCGSessionModal
        ref={sessionModalRef}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
        onSuccess={handleUploadSuccess}
      />
    </PageConfig>
  );
};

export default MentalHealthDashboard;
